import { Facebook, Instagram, Mail, Linkedin  } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { Logo } from "./Logo";

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const Footer = () => {
  const { colors, theme } = useTheme();
  
  const socialLinks = [
    { href: "https://www.facebook.com/agapeentertainment/", icon: Facebook, label: "Facebook" },
    { href: "https://x.com/agape_entertain", icon: XIcon, label: "Twitter" },
    { href: "https://www.instagram.com/agapeentertainment/", icon: Instagram, label: "Instagram" },
    { href: "https://www.linkedin.com/company/agapeentertainmentin/", icon: Linkedin, label: "Linkedin" },
    // { href: "mailto:info@agapeentertainment.in", icon: Mail, label: "Email" }
  ];

  const handleNavigation = (path: string) => {
    const currentPath = window.location.pathname;
    
    // Handle direct page routes
    if (path === "/privacy-policy" || path === "/terms-conditions") {
      window.location.href = path;
      return;
    }
    
    // Handle home page sections
    if (path.startsWith("#")) {
      const sectionId = path.substring(1);
      if (currentPath !== "/") {
        // Navigate to home page with hash
        window.location.href = `/${path}`;
      } else {
        // Already on home page, just scroll to section
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
      return;
    }
    
    // Handle home page navigation
    if (path === "/") {
      window.location.href = "/";
      return;
    }
  };

  const quickLinks = [
    { path: "/", label: "Home" },
    { path: "#services", label: "Services" },
    { path: "#about-us", label: "About Us" },
    { path: "/privacy-policy", label: "Privacy Policy" },
    { path: "#contact", label: "Contact Us" },
    { path: "/terms-conditions", label: "Terms & Conditions" },
    { path: "#our-mission", label: "Our Mission" },
  ];

  return (
    <footer 
      className="py-12 border-t relative overflow-hidden transition-colors duration-300"
      style={{ 
        backgroundColor: colors.background,
        borderColor: `${colors.border}60`,
        color: colors.foreground
      }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <motion.div 
          className="absolute top-0 left-0 w-64 h-64 rounded-full blur-3xl"
          style={{ 
            background: `radial-gradient(circle, ${colors.primary}10 0%, transparent 70%)`
          }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-0 right-0 w-64 h-64 rounded-full blur-3xl"
          style={{ 
            background: `radial-gradient(circle, ${colors.accentLight}10 0%, transparent 70%)`
          }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8 lg:gap-12">
          <motion.div
            className="md:col-span-1"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-4">
              <Logo className="h-12 w-auto" />
            </div>
            <p className="leading-relaxed" style={{ color: colors.secondary }}>
              At Agape, we strive to amplify voices, nurture stories, and champion cinematic excellence-one frame at a time.
            </p>
          </motion.div>
          
          <motion.div
            className="md:col-span-1"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xl font-semibold mb-6" style={{ color: colors.foreground }}>
              Quick Links
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <motion.button
                    onClick={() => handleNavigation(link.path)}
                    className="transition-colors relative group hover:underline text-sm text-left w-full"
                    style={{ color: colors.secondary }}
                    whileHover={{ x: 5, color: colors.primary }}
                  >
                    {link.label}
                    <motion.div
                      className="absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300"
                      style={{ backgroundColor: colors.primary }}
                    />
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            className="md:col-span-1"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h4 className="text-xl font-semibold mb-6" style={{ color: colors.foreground }}>
              Connect With Us
            </h4>
            
            {/* Single row layout for all screen sizes */}
            <div className="flex items-center gap-4 flex-wrap">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('http') ? "_blank" : undefined}
                  rel={social.href.startsWith('http') ? "noopener noreferrer" : undefined}
                  className="flex items-center justify-center transition-all duration-300 p-3 rounded-full border"
                  style={{ 
                    color: colors.secondary,
                    borderColor: `${colors.border}60`,
                    backgroundColor: theme === 'light' ? colors.card : `${colors.card}20`
                  }}
                  whileHover={{ 
                    scale: 1.1,
                    color: colors.primary,
                    boxShadow: `0 4px 15px ${colors.primary}20`,
                    borderColor: `${colors.primary}50`,
                    backgroundColor: theme === 'light' ? colors.muted : `${colors.card}40`
                  }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
        
        <motion.div
          className="mt-16 pt-8 border-t text-center"
          style={{ borderColor: `${colors.border}60` }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <motion.p
            style={{ color: colors.secondary }}
            animate={{
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            &copy; {new Date().getFullYear()} Agape Entertainment. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </footer>
  );
};